import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';

import { hasAlertSelector } from '@/recoil';
import SvgSelector from '@/components/Svg/SvgSelector';
import { FontStyle } from '@/utils/StyleUtil';
import { _isInputPageWithBottomButton } from '@/utils/PageChecker';
import { RESIZED_SCREEN_TABLET, TABLET } from '@/models/breakPoints';
import BorderRoundButton from '@/components/Button/BorderRoundButton';

type TLeftButton = 'back' | 'logo' | 'tempBack';
type TRightButton = 'home' | 'alarm' | 'search' | 'button';

interface ILayout {
    children: React.ReactNode;
    leftButton?: TLeftButton;
    rightButton?: TRightButton;
    title?: string;
    onClickLeftButton?: Function;
    onClickRightButton?: Function;
    backgroundColor?: string;
}

const TopMainLayout = ({
    children,
    leftButton,
    rightButton,
    title,
    onClickLeftButton,
    onClickRightButton,
    backgroundColor = 'transparent',
}: ILayout) => {
    const hasAlert = useRecoilValue(hasAlertSelector);
    const router = useRouter();

    const onClickLogo = () => {
        router.push('/');
    };

    const onClickBack = () => {
        if (onClickLeftButton) {
            onClickLeftButton();
        } else {
            router.back();
        }
    };

    const onClickBackTemp = () => {
        if (onClickLeftButton) {
            onClickLeftButton();
        } else {
            const prevPage = window.sessionStorage.getItem('prevPage');
            if (prevPage === '/event/[id]') {
                router.back();
            } else {
                router.push('/');
            }
        }
    };

    const handleClickRightButton = () => {
        if (onClickRightButton) onClickRightButton();
    };

    const renderLeftButton = () => {
        switch (leftButton) {
            case 'back':
                return (
                    <button onClick={onClickBack}>
                        <Image src={'/img/layout/goBack@3x.png'} alt="back" width={28} height={28} />
                    </button>
                );
            case 'tempBack':
                return (
                    <button onClick={onClickBackTemp}>
                        <Image src={'/img/layout/goBack@3x.png'} alt="back" width={28} height={28} />
                    </button>
                );
            case 'logo':
                return (
                    <Image
                        src="/img/logo/logo_main_text@3x.png"
                        alt="logo"
                        width={111}
                        height={23}
                        onClick={onClickLogo}
                    />
                );
            default:
                return <EmptyDiv />;
        }
    };

    const renderRightButton = () => {
        switch (rightButton) {
            case 'home':
                return (
                    <Link href="/">
                        <a>
                            <Image src="/img/layout/toHome@3x.png" alt="home" width={28} height={28} />
                        </a>
                    </Link>
                );
            case 'alarm':
                return (
                    <Link href="/notification">
                        <a>
                            {hasAlert ? (
                                <Image src={'/img/layout/alert_dot@3x.png'} alt="alert" width={28} height={28} />
                            ) : (
                                <Image src={'/img/layout/alert@3x.png'} alt="alert dot" width={28} height={28} />
                            )}
                        </a>
                    </Link>
                );
            case 'search':
                return (
                    <Link href="/ina/search">
                        <a>
                            <SvgSelector svg="search" width={20} height={20} stroke="#222222" strokeWidth={1.6} />
                        </a>
                    </Link>
                );
            case 'button':
                return (
                    <BorderRoundButton
                        text="프로필보기"
                        borderRadius={22}
                        color="#444444"
                        onClick={handleClickRightButton}
                    />
                );
            default:
                return <EmptyDiv />;
        }
    };

    return (
        <Container id="top_main_layout" isInputPage={_isInputPageWithBottomButton()} backgroundColor={backgroundColor}>
            <HeaderContainer>
                {renderLeftButton()}
                {title && <Title as={title === '플래너 프로필' ? 'div' : 'h1'}>{title}</Title>}
                {renderRightButton()}
            </HeaderContainer>
            {children}
        </Container>
    );
};

const Container = styled.div<{ isInputPage: boolean; backgroundColor: string }>`
    height: 100%;
    ${({ isInputPage }) => isInputPage && 'overflow: auto'};
    padding-top: 56px;
    ${({ backgroundColor }) => `background-color: ${backgroundColor}`};
`;

const Title = styled.h1`
    ${FontStyle(16, 24, '400', '#222222')}
`;

const EmptyDiv = styled.div`
    width: 28px;
`;

const HeaderContainer = styled.div`
    position: fixed;
    top: 0;
    ${({ theme }) => theme.flex.flexOnly};
    ${({ theme }) => theme.flex.justifyBetween};
    width: 100%;
    height: 56px;
    padding: 0 20px;
    background-color: #ffffff;
    z-index: 900;

    @media (min-width: ${TABLET}px) {
        max-width: ${RESIZED_SCREEN_TABLET}px;
    }
`;

export default React.memo(TopMainLayout);
